import { useState, useEffect, useRef } from 'react';
import { writeOrgFile, deleteOrgFile, listOrgCollection } from '../../storage/FileSystemAdapter';
import { appendChangeLog } from '../../utils/changeLog';
import { callWorker } from '../../utils/workerClient';
import { ASSET_STATUSES } from '../../utils/enums';
import PeopleSelector from '../shared/PeopleSelector';
import TagInput from '../shared/TagInput';
import EntitySelector from '../shared/EntitySelector';
import ConfirmDialog from '../shared/ConfirmDialog';
import SaveErrorDialog from '../shared/SaveErrorDialog';
import AISuggestButton from '../shared/AISuggestButton';
import { BTN_PRIMARY, BTN_SECONDARY, BTN_DESTRUCTIVE_OUTLINE, TEXT_MUTED, TEXT_DISABLED, BORDER_DEFAULT, TEXT_HEADING, BG_SURFACE, BRAND_PRIMARY_DARK, ERROR_TEXT, ERROR_BG, ERROR_BORDER, FIELD_ERROR_BORDER, UNSAVED_AMBER, SURFACE_WHITE, STATUS_INFO, FONT_SECTION_TITLE, FONT_BODY, FONT_BODY_STRONG, FONT_LABEL, FONT_SMALL, FONT_BUTTON } from '../../theme';

// ── CONSTANTS ─────────────────────────────────────────────────────────────────
const ASSET_TYPES      = ['Information','Software','Hardware','Service','People','Premises','Other'];
const LEVELS           = ['Low','Medium','High'];
const DEFAULT_CLASSIFICATIONS = ['Public','Internal','Confidential','Restricted'];
const DIMENSION_LEVELS = [
  { value: 1, label: 'Low' },
  { value: 2, label: 'Medium' },
  { value: 3, label: 'High' },
  { value: 4, label: 'Critical' },
];

// ── I18N ──────────────────────────────────────────────────────────────────────
const T = {
  es: {
    titleCreate:          'Nuevo activo',
    titleEdit:            'Editar activo',
    titleView:            'Ver activo',
    s1:                   'Identificación',
    s2:                   'Clasificación y valor',
    s3:                   'Responsabilidad',
    s4:                   'Ubicación y relaciones',
    s5:                   'Ciclo de vida',
    fRef:                 'Referencia',
    fName:                'Nombre',
    fType:                'Tipo',
    fDescription:         'Descripción',
    fOwner:               'Propietario',
    fCustodian:           'Custodio',
    fCriticality:         'Criticidad',
    fClassification:      'Clasificación',
    fContainsPersonalData:'Contiene datos personales (RGPD)',
    fDataSubjectsCategory:'Categoría de interesados',
    fDataCategoriesProcessed:'Categorías de datos tratados',
    fCrossBorderTransfer: 'Transferencia internacional de datos',
    fLegalBasisForProcessing:'Base legal del tratamiento',
    fContainsSpecialCategoryData:'Incluye categorías especiales (Art.9 RGPD)',
    fRetentionPeriod:     'Plazo de conservación',
    fLocation:            'Ubicación',
    fLinkedSupplierIds:   'Proveedores vinculados',
    fLinkedDocumentIds:   'Documentos vinculados',
    fStatus:              'Estado',
    fReviewDate:          'Fecha de revisión',
    btnSave:              'Guardar',
    btnCancel:            'Cancelar',
    btnDelete:            'Eliminar activo',
    btnConfirmDelete:     '¿Confirmar eliminación?',
    btnCancelDelete:      'No, cancelar',
    saving:               'Guardando…',
    saveOk:               '✓ Guardado',
    errorRequired:        'Campo obligatorio',
    errorSave:            'Error al guardar. Inténtalo de nuevo.',
    errorDelete:          'Error al eliminar.',
    required:             '*',
    requiredLegend:       '* Campos obligatorios',
    dataCategoryPlaceholder: 'Añadir categoría de dato y pulsar Enter…',
    retentionPlaceholder: 'p.ej. 5 años desde el fin de la relación',
    legalBasisPlaceholder:'p.ej. Consentimiento (Art. 6.1.a RGPD)',
    dataSubjectsPlaceholder:'p.ej. clientes, empleados, proveedores',
    placeholderIds:       'Buscar…',
    generating:           'Generando…',
    unsavedLabel:         'Cambios sin guardar',
    fDimensions:          'Dimensiones CIA',
    fConfidentiality:     'Confidencialidad',
    fIntegrity:           'Integridad',
    fAvailability:        'Disponibilidad',
    aiSuggesting:         'Sugiriendo…',
    aiError:              'No se pudo obtener sugerencia. Inténtalo de nuevo.',
    aiErrorOverloaded:    'Servidores ocupados, inténtalo de nuevo en un momento.',
    aiConsentRequired:    'Activa el procesamiento con IA en Configuración para usar esta función.',
  },
  en: {
    titleCreate:          'New asset',
    titleEdit:            'Edit asset',
    titleView:            'View asset',
    s1:                   'Identification',
    s2:                   'Classification and value',
    s3:                   'Ownership',
    s4:                   'Location and relationships',
    s5:                   'Lifecycle',
    fRef:                 'Reference',
    fName:                'Name',
    fType:                'Type',
    fDescription:         'Description',
    fOwner:               'Owner',
    fCustodian:           'Custodian',
    fCriticality:         'Criticality',
    fClassification:      'Classification',
    fContainsPersonalData:'Contains personal data (GDPR)',
    fDataSubjectsCategory:'Data subjects category',
    fDataCategoriesProcessed:'Data categories processed',
    fCrossBorderTransfer: 'Cross-border data transfer',
    fLegalBasisForProcessing:'Legal basis for processing',
    fContainsSpecialCategoryData:'Includes special category data (Art.9 GDPR)',
    fRetentionPeriod:     'Retention period',
    fLocation:            'Location',
    fLinkedSupplierIds:   'Linked suppliers',
    fLinkedDocumentIds:   'Linked documents',
    fStatus:              'Status',
    fReviewDate:          'Review date',
    btnSave:              'Save',
    btnCancel:            'Cancel',
    btnDelete:            'Delete asset',
    btnConfirmDelete:     'Confirm delete?',
    btnCancelDelete:      'No, cancel',
    saving:               'Saving…',
    saveOk:               '✓ Saved',
    errorRequired:        'Required',
    errorSave:            'Save failed. Please try again.',
    errorDelete:          'Delete failed.',
    required:             '*',
    requiredLegend:       '* Required fields',
    dataCategoryPlaceholder: 'Add data category and press Enter…',
    retentionPlaceholder: 'e.g. 5 years after end of relationship',
    legalBasisPlaceholder:'e.g. Consent (Art. 6(1)(a) GDPR)',
    dataSubjectsPlaceholder:'e.g. customers, employees, suppliers',
    placeholderIds:       'Search…',
    generating:           'Generating…',
    unsavedLabel:         'Unsaved changes',
    fDimensions:          'CIA Dimensions',
    fConfidentiality:     'Confidentiality',
    fIntegrity:           'Integrity',
    fAvailability:        'Availability',
    aiSuggesting:         'Suggesting…',
    aiError:              'Could not get suggestion. Please try again.',
    aiErrorOverloaded:    'Servers are busy, please try again in a moment.',
    aiConsentRequired:    'Enable AI processing in Settings to use this feature.',
  },
};

// ── HELPERS ───────────────────────────────────────────────────────────────────
function todayStr() { return new Date().toISOString().slice(0, 10); }
function yyyymmdd() { return todayStr().replace(/-/g, ''); }
function oneYearFromToday() {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
}

async function generateAssetIds(orgId, assetPrefix) {
  let assets = [];
  try { assets = await listOrgCollection(orgId, 'assets'); } catch { /* empty */ }

  const day = yyyymmdd();
  const dayPrefix = `asset-${day}-`;
  let maxFileSeq = 0;
  assets.forEach((a) => {
    if (a.id && a.id.startsWith(dayPrefix)) {
      const n = parseInt(a.id.slice(dayPrefix.length), 10);
      if (!isNaN(n) && n > maxFileSeq) maxFileSeq = n;
    }
  });
  const fileId = `${dayPrefix}${String(maxFileSeq + 1).padStart(3, '0')}`;

  const year = new Date().getFullYear();
  const yearPrefix = `${assetPrefix}-${year}-`;
  let maxRefSeq = 0;
  assets.forEach((a) => {
    if (a.ref && a.ref.startsWith(yearPrefix)) {
      const n = parseInt(a.ref.slice(yearPrefix.length), 10);
      if (!isNaN(n) && n > maxRefSeq) maxRefSeq = n;
    }
  });
  const ref = `${yearPrefix}${String(maxRefSeq + 1).padStart(3, '0')}`;

  return { fileId, ref };
}

// ── INITIAL STATE ─────────────────────────────────────────────────────────────
const EMPTY_FORM = {
  name: '', type: 'Information', description: '',
  owner: '', custodian: '', criticality: 'Medium',
  classification: 'Internal', personalDataContained: false,
  dataSubjectsCategory: '', dataCategoriesProcessed: [],
  crossBorderTransfer: false, legalBasisForProcessing: '',
  containsSpecialCategoryData: false, retentionPeriod: '',
  location: '',
  status: 'Active', reviewDate: '',
  dimensions: { confidentiality: null, integrity: null, availability: null },
  linkedSupplierIds: [], linkedDocumentIds: [],
  norms: [], tags: [],
};

const DIM_STRING_MAP = { Low: 1, Medium: 2, High: 3, Critical: 4 };

function migrateDim(v) {
  if (v === null || v === undefined) return null;
  if (typeof v === 'number') return v;
  return DIM_STRING_MAP[v] ?? null;
}

function assetToForm(a) {
  return {
    name:               a.name               || '',
    type:               a.type === 'Services' ? 'Service' : (a.type || 'Information'),
    description:        a.description        || '',
    owner:              a.owner              || '',
    custodian:          a.custodian          || '',
    criticality:        a.criticality        || 'Medium',
    classification:     a.classification     || 'Internal',
    personalDataContained: a.personalDataContained !== undefined
      ? !!a.personalDataContained
      : !!a.containsPersonalData,
    dataSubjectsCategory:       a.dataSubjectsCategory         || '',
    dataCategoriesProcessed:    Array.isArray(a.dataCategoriesProcessed) ? a.dataCategoriesProcessed : [],
    crossBorderTransfer:        !!a.crossBorderTransfer,
    legalBasisForProcessing:    a.legalBasisForProcessing      || '',
    containsSpecialCategoryData:!!a.containsSpecialCategoryData,
    retentionPeriod:            a.retentionPeriod              || '',
    location:           a.location           || '',
    status:             ASSET_STATUSES.includes(a.status) ? a.status : 'Active',
    reviewDate:         a.reviewDate         || oneYearFromToday(),
    dimensions: {
      confidentiality: migrateDim(a.dimensions?.confidentiality),
      integrity:       migrateDim(a.dimensions?.integrity),
      availability:    migrateDim(a.dimensions?.availability),
    },
    linkedSupplierIds:  Array.isArray(a.linkedSupplierIds) ? a.linkedSupplierIds : [],
    linkedDocumentIds:  Array.isArray(a.linkedDocumentIds) ? a.linkedDocumentIds : [],
    norms:              Array.isArray(a.norms) ? a.norms : [],
    tags:               Array.isArray(a.tags) ? a.tags : [],
  };
}

// Silent migration for the appendChangeLog diff. Existing asset records on
// disk may still carry legacy keys removed in the 2026-05-13 cleanup;
// stripping them from the previous snapshot prevents spurious
// "value → undefined" entries in the audit trail when an old record is edited.
const LEGACY_ASSET_FIELDS = [
  'purpose', 'sensitivity', 'legalRequirements', 'financialValue',
  'format', 'acquisitionDate', 'retirementDate', 'lifecycleStage',
  'rto', 'rpo', 'relatedProcesses',
  'containsPersonalData', 'riskIds', 'controlIds',
];
function stripLegacyAssetFields(record) {
  const clone = { ...record };
  for (const k of LEGACY_ASSET_FIELDS) delete clone[k];
  return clone;
}

// ── SHARED STYLES ─────────────────────────────────────────────────────────────
const labelSt = {
  ...FONT_SMALL,
  display: 'block', color: TEXT_MUTED,
  letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '0.3rem',
};
const inputSt = (hasErr) => ({
  ...FONT_BODY,
  width: '100%', padding: '0.55rem 0.7rem',
  border: `1px solid ${hasErr ? FIELD_ERROR_BORDER : BORDER_DEFAULT}`,
  borderRadius: 6, background: SURFACE_WHITE,
  color: TEXT_HEADING, outline: 'none', boxSizing: 'border-box',
});
const textareaSt = (hasErr) => ({ ...inputSt(hasErr), resize: 'vertical', minHeight: 72 });
const selectSt = { ...inputSt(false), cursor: 'pointer' };
const sectionHeading = {
  ...FONT_BODY_STRONG, color: TEXT_HEADING,
  margin: '0 0 1rem', paddingBottom: '0.5rem',
  borderBottom: `1px solid ${BORDER_DEFAULT}`,
};
const fieldWrap = { marginBottom: '1rem' };
const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1.5rem' };

function ErrMsg({ msg }) {
  if (!msg) return null;
  return (
    <p style={{ ...FONT_SMALL, color: ERROR_TEXT, margin: '0.2rem 0 0' }}>
      {msg}
    </p>
  );
}

// ── COMPONENT ─────────────────────────────────────────────────────────────────
export default function AssetForm({ asset, mode, org, lang, onSave, onCancel, onFormChange, saveRequestTick, dirty }) {
  const t = T[lang] || T.en;
  const isEdit = !!asset;
  const isView = mode === 'view';
  const [currentMode, setCurrentMode] = useState(mode || (isEdit ? 'edit' : 'create'));
  const [savedRecord, setSavedRecord] = useState(isEdit ? asset : null);
  const hasChangedRef = useRef(false);

  const classifications = (Array.isArray(org.documentClassification) && org.documentClassification.length > 0)
    ? org.documentClassification
    : DEFAULT_CLASSIFICATIONS;

  const [form, setForm] = useState(() => {
    if (isEdit) return assetToForm(asset);
    const defaultClass = classifications.includes('Internal')
      ? 'Internal'
      : (classifications[0] || 'Internal');
    return { ...EMPTY_FORM, reviewDate: oneYearFromToday(), classification: defaultClass };
  });
  const [fileId,            setFileId]            = useState(isEdit ? asset.id  : null);
  const [ref,               setRef]               = useState(isEdit ? asset.ref : null);
  const [errors,            setErrors]            = useState({});
  const [saveState,         setSaveState]         = useState('idle');
  const [saveErrorOpen,     setSaveErrorOpen]     = useState(false);
  const [confirmState,      setConfirmState]      = useState(null);
  const [deleteError,       setDeleteError]       = useState(null);
  const [supplierOptions,  setSupplierOptions]  = useState([]);
  const [documentOptions,  setDocumentOptions]  = useState([]);
  const [aiSuggesting,      setAiSuggesting]      = useState(false);
  const [aiError,           setAiError]           = useState(null);

  useEffect(() => {
    if (isEdit) return;
    const assetPrefix = (org.refPrefixes && org.refPrefixes.asset) || 'ast';
    generateAssetIds(org.id, assetPrefix)
      .then(({ fileId: fid, ref: r }) => { setFileId(fid); setRef(r); })
      .catch(() => { setFileId(`asset-${yyyymmdd()}-001`); setRef(`${assetPrefix}-${new Date().getFullYear()}-001`); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    listOrgCollection(org.id, 'suppliers')
      .then((list) => setSupplierOptions(list.map((s) => ({ id: s.id, label: s.name || s.ref || s.id }))))
      .catch(() => setSupplierOptions([]));
    listOrgCollection(org.id, 'documents')
      .then((list) => setDocumentOptions(list.map((d) => ({ id: d.id, label: d.title || d.ref || d.id }))))
      .catch(() => setDocumentOptions([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org.id]);

  const markChange = () => {
    if (!isView && !hasChangedRef.current && onFormChange) {
      hasChangedRef.current = true;
      onFormChange();
    }
  };

  const sf  = (field) => (val) => { markChange(); setForm((p) => ({ ...p, [field]: val })); };
  const hic = (field) => (e)   => sf(field)(e.target.value);
  const setDim = (key) => (e) => {
    markChange();
    const raw = e.target.value;
    setForm((p) => ({ ...p, dimensions: { ...p.dimensions, [key]: raw === '' ? null : parseInt(raw, 10) } }));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name  = t.errorRequired;
    if (!form.owner)       errs.owner = t.errorRequired;
    return errs;
  };

  const handleSave = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    if (!fileId || !ref) return;
    setSaveState('saving');
    const now = new Date().toISOString();
    try {
      const payload = {
        id:                  fileId,
        orgId:               org.id,
        ref,
        name:                form.name.trim(),
        type:                form.type,
        description:         form.description.trim(),
        owner:               form.owner,
        custodian:           form.custodian || null,
        criticality:         form.criticality,
        classification:      form.classification,
        personalDataContained:form.personalDataContained,
        dataSubjectsCategory:       form.dataSubjectsCategory.trim() || null,
        dataCategoriesProcessed:    form.dataCategoriesProcessed,
        crossBorderTransfer:        form.crossBorderTransfer,
        legalBasisForProcessing:    form.legalBasisForProcessing.trim() || null,
        containsSpecialCategoryData:form.containsSpecialCategoryData,
        retentionPeriod:            form.retentionPeriod.trim() || null,
        location:            form.location.trim(),
        linkedSupplierIds:   form.linkedSupplierIds,
        linkedDocumentIds:   form.linkedDocumentIds,
        dimensions:          { ...form.dimensions },
        status:              form.status,
        reviewDate:          form.reviewDate,
        norms:               form.norms,
        tags:                form.tags,
        createdAt:           savedRecord?.createdAt  || now,
        updatedAt:           now,
      };
      payload.changeLog = savedRecord
        ? appendChangeLog(stripLegacyAssetFields(savedRecord), payload, org.consultantPersonId || 'system')
        : [];
      await writeOrgFile(org.id, `assets/${fileId}.json`, payload);
      setSavedRecord(payload);
      if (currentMode !== 'edit') setCurrentMode('edit');
      onSave(fileId);
      setSaveState('ok');
      setTimeout(() => setSaveState('idle'), 2000);
    } catch {
      setSaveState('error');
      // §11 rule 37 — blocking dialog on per-entity save failure.
      setSaveErrorOpen(true);
      setTimeout(() => setSaveState('idle'), 3000);
    }
  };

  // §11 rule 37 — discard reverts to last saved snapshot and exits the form.
  const handleDiscardChanges = () => {
    setSaveErrorOpen(false);
    if (savedRecord) {
      setForm(assetToForm(savedRecord));
      hasChangedRef.current = false;
      setErrors({});
    }
    onCancel();
  };
  const handleRetrySave = () => {
    setSaveErrorOpen(false);
    handleSave();
  };

  useEffect(() => {
    if (saveRequestTick > 0) handleSave();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveRequestTick]);

  const handleDelete = async () => {
    try {
      await deleteOrgFile(org.id, `assets/${fileId}.json`);
      onSave(null);
    } catch {
      setDeleteError(t.errorDelete);
      setConfirmState(null);
    }
  };

  // AI Suggest — Section 2 (criticality, classification, CIA dimensions,
  // personalDataContained). Mirrors RiskForm Section 1's handleAISuggest in
  // structure. Unlike that one, results are applied unconditionally: the
  // consultant clicks Suggest with intent to overwrite the defaults.
  const handleAISuggest = async () => {
    setAiSuggesting(true);
    setAiError(null);
    const classificationOptions = classifications;
    const orgLines = [];
    if (org.sector)        orgLines.push(`Organization sector: ${org.sector}`);
    if (org.size)          orgLines.push(`Organization size: ${org.size}`);
    if (Array.isArray(org.technologies) && org.technologies.length > 0) {
      orgLines.push(`Technologies: ${org.technologies.join(', ')}`);
    } else if (typeof org.technologies === 'string' && org.technologies) {
      orgLines.push(`Technologies: ${org.technologies}`);
    }
    if (org.country)       orgLines.push(`Country: ${org.country}`);
    if (org.jurisdiction)  orgLines.push(`Jurisdiction: ${org.jurisdiction}`);
    const orgBlock = orgLines.length > 0 ? `\nOrganization context:\n${orgLines.join('\n')}` : '';
    const prompt = `You are an ISO 27001:2022 asset classification expert.\nGiven the following asset, suggest the most appropriate classification values.\nRespond ONLY with a valid JSON object, no markdown, no preamble, no explanation.\nAsset type: ${form.type}\nAsset description: ${form.description || '(none)'}\nAvailable classifications: ${JSON.stringify(classificationOptions)}${orgBlock}\nReturn exactly this JSON structure:\n{\n"criticality": "Low|Medium|High",\n"classification": "one of the available classifications",\n"dimensions": { "confidentiality": 1, "integrity": 1, "availability": 1 },\n"personalDataContained": false\n}\nDimensions values are integers in [1,2,3,4] where 1=Low, 2=Medium, 3=High, 4=Critical.`;
    const result = await callWorker({ prompt, maxTokens: 400 });
    if (!result.ok) {
      const busy = result.error === 'overloaded' || result.error === 'timeout';
      setAiError(busy ? t.aiErrorOverloaded : t.aiError);
      setAiSuggesting(false);
      return;
    }
    let parsed;
    try { parsed = JSON.parse(result.text); } catch {
      setAiError(t.aiError);
      setAiSuggesting(false);
      return;
    }
    markChange();
    setForm((p) => {
      const next = { ...p, dimensions: { ...p.dimensions } };
      if (typeof parsed.criticality === 'string' && LEVELS.includes(parsed.criticality)) {
        next.criticality = parsed.criticality;
      }
      if (typeof parsed.classification === 'string' && classificationOptions.includes(parsed.classification)) {
        next.classification = parsed.classification;
      }
      if (parsed.dimensions && typeof parsed.dimensions === 'object') {
        const { confidentiality, integrity, availability } = parsed.dimensions;
        if (Number.isInteger(confidentiality) && confidentiality >= 1 && confidentiality <= 4) {
          next.dimensions.confidentiality = confidentiality;
        }
        if (Number.isInteger(integrity) && integrity >= 1 && integrity <= 4) {
          next.dimensions.integrity = integrity;
        }
        if (Number.isInteger(availability) && availability >= 1 && availability <= 4) {
          next.dimensions.availability = availability;
        }
      }
      if (parsed.personalDataContained === true || parsed.personalDataContained === false) {
        next.personalDataContained = parsed.personalDataContained;
      }
      return next;
    });
    setAiSuggesting(false);
  };

  const disabled = saveState === 'saving' || isView;

  const btnPrimary = {
    ...BTN_PRIMARY, ...FONT_BUTTON,
    background: saveState === 'saving' ? TEXT_MUTED : saveState === 'ok' ? BRAND_PRIMARY_DARK : BTN_PRIMARY.background,
    border: 'none', borderRadius: 8,
    padding: '0.55rem 1.25rem', cursor: disabled ? 'default' : 'pointer',
  };
  const btnSecondary = {
    ...BTN_SECONDARY, ...FONT_BUTTON,
    borderRadius: 8,
    padding: '0.55rem 1.25rem', cursor: disabled ? 'default' : 'pointer',
  };

  return (
    <div style={{ background: BG_SURFACE, borderRadius: 12, border: `1px solid ${BORDER_DEFAULT}`, padding: '2rem' }}>
      <ConfirmDialog
        open={confirmState !== null}
        message={t.btnConfirmDelete}
        onConfirm={handleDelete}
        onCancel={() => setConfirmState(null)}
        confirmLabel={confirmState?.confirmLabel}
      />

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ ...FONT_SECTION_TITLE, color: TEXT_HEADING, margin: 0 }}>
          {isView ? t.titleView : (currentMode === 'edit' ? t.titleEdit : t.titleCreate)}
          {ref && (
            <span style={{ ...FONT_LABEL, color: TEXT_MUTED, marginLeft: '0.75rem' }}>
              {ref}
            </span>
          )}
          {!ref && !isView && currentMode !== 'edit' && (
            <span style={{ ...FONT_SMALL, color: TEXT_DISABLED, marginLeft: '0.5rem' }}>
              {t.generating}
            </span>
          )}
        </h2>
        {!isView && (
          <p style={{ ...FONT_SMALL, color: TEXT_DISABLED, margin: '0.35rem 0 0' }}>
            {t.requiredLegend}
          </p>
        )}
      </div>

      <fieldset disabled={disabled} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
      {/* ── Section 1: Identification ──────────────────────────────────────── */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={sectionHeading}>{t.s1}</h3>
        <div style={grid2}>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fName} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
            <input value={form.name} onChange={hic('name')} disabled={disabled} style={inputSt(!!errors.name)} />
            <ErrMsg msg={errors.name} />
          </div>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fType}</label>
            <select value={form.type} onChange={hic('type')} disabled={disabled} style={selectSt}>
              {ASSET_TYPES.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div style={{ ...fieldWrap, gridColumn: 'span 2' }}>
            <label style={labelSt}>{t.fDescription}</label>
            <textarea value={form.description} onChange={hic('description')} disabled={disabled} style={textareaSt(false)} />
          </div>
        </div>
      </div>

      {/* ── Section 2: Classification & Value ─────────────────────────────── */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '1rem', ...sectionHeading }}>
          <span>{t.s2}</span>
          <AISuggestButton onClick={handleAISuggest} loading={aiSuggesting} disabled={disabled || !form.name.trim() || !org.aiProcessingConsent} lang={lang} />
          {!org.aiProcessingConsent && <span style={{ ...FONT_SMALL, color: TEXT_MUTED }}>{t.aiConsentRequired}</span>}
        </div>
        {aiError && <p style={{ ...FONT_SMALL, color: ERROR_TEXT, margin: '-0.5rem 0 1rem' }}>{aiError}</p>}
        <div style={grid2}>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fCriticality}</label>
            <select value={form.criticality} onChange={hic('criticality')} disabled={disabled} style={selectSt}>
              {LEVELS.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fClassification}</label>
            <select value={form.classification} onChange={hic('classification')} disabled={disabled} style={selectSt}>
              {classifications.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div style={{ ...fieldWrap, display: 'flex', alignItems: 'center', gap: '0.5rem', gridColumn: 'span 2' }}>
            <input
              type="checkbox" id="ast-cpd"
              checked={form.personalDataContained}
              onChange={(e) => sf('personalDataContained')(e.target.checked)}
              disabled={disabled}
              style={{ width: 16, height: 16, flexShrink: 0 }}
            />
            <label htmlFor="ast-cpd" style={{ ...labelSt, marginBottom: 0, textTransform: 'none', letterSpacing: 0, fontSize: '0.82rem', cursor: 'pointer' }}>
              {t.fContainsPersonalData}
            </label>
          </div>
        </div>
        {/* GDPR fields — visible only when personalDataContained=true (GRC_UI_PATTERNS §14) */}
        {form.personalDataContained && (
          <div style={{ marginTop: '1rem' }}>
            <div style={grid2}>
              <div style={fieldWrap}>
                <label style={labelSt}>{t.fDataSubjectsCategory}</label>
                <input
                  value={form.dataSubjectsCategory}
                  onChange={hic('dataSubjectsCategory')}
                  placeholder={t.dataSubjectsPlaceholder}
                  disabled={disabled}
                  style={inputSt(false)}
                />
              </div>
              <div style={fieldWrap}>
                <label style={labelSt}>{t.fLegalBasisForProcessing}</label>
                <input
                  value={form.legalBasisForProcessing}
                  onChange={hic('legalBasisForProcessing')}
                  placeholder={t.legalBasisPlaceholder}
                  disabled={disabled}
                  style={inputSt(false)}
                />
              </div>
              <div style={{ ...fieldWrap, gridColumn: 'span 2' }}>
                <label style={labelSt}>{t.fDataCategoriesProcessed}</label>
                <TagInput
                  tags={form.dataCategoriesProcessed}
                  onAdd={(v) => sf('dataCategoriesProcessed')([...form.dataCategoriesProcessed, v])}
                  onRemove={(v) => sf('dataCategoriesProcessed')(form.dataCategoriesProcessed.filter((x) => x !== v))}
                  onUpdate={(oldValue, newValue) => sf('dataCategoriesProcessed')(form.dataCategoriesProcessed.map((x) => x === oldValue ? newValue : x))}
                  placeholder={t.dataCategoryPlaceholder}
                  disabled={disabled}
                />
              </div>
              <div style={fieldWrap}>
                <label style={labelSt}>{t.fRetentionPeriod}</label>
                <input
                  value={form.retentionPeriod}
                  onChange={hic('retentionPeriod')}
                  placeholder={t.retentionPlaceholder}
                  disabled={disabled}
                  style={inputSt(false)}
                />
              </div>
              <div style={{ ...fieldWrap, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox" id="ast-cbt"
                  checked={form.crossBorderTransfer}
                  onChange={(e) => sf('crossBorderTransfer')(e.target.checked)}
                  disabled={disabled}
                  style={{ width: 16, height: 16, flexShrink: 0 }}
                />
                <label htmlFor="ast-cbt" style={{ ...labelSt, marginBottom: 0, textTransform: 'none', letterSpacing: 0, fontSize: '0.82rem', cursor: 'pointer' }}>
                  {t.fCrossBorderTransfer}
                </label>
              </div>
              <div style={{ ...fieldWrap, display: 'flex', alignItems: 'center', gap: '0.5rem', gridColumn: 'span 2' }}>
                <input
                  type="checkbox" id="ast-scd"
                  checked={form.containsSpecialCategoryData}
                  onChange={(e) => sf('containsSpecialCategoryData')(e.target.checked)}
                  disabled={disabled}
                  style={{ width: 16, height: 16, flexShrink: 0 }}
                />
                <label htmlFor="ast-scd" style={{ ...labelSt, marginBottom: 0, textTransform: 'none', letterSpacing: 0, fontSize: '0.82rem', cursor: 'pointer' }}>
                  {t.fContainsSpecialCategoryData}
                </label>
              </div>
            </div>
          </div>
        )}
        {/* Dimensions subsection */}
        <div style={{ marginTop: '1.25rem' }}>
          <p style={{ ...labelSt, marginBottom: '0.75rem' }}>{t.fDimensions}</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 1.5rem' }}>
            <div style={fieldWrap}>
              <label style={labelSt}>{t.fConfidentiality}</label>
              <select value={form.dimensions.confidentiality != null ? String(form.dimensions.confidentiality) : ''} onChange={setDim('confidentiality')} disabled={disabled} style={selectSt}>
                <option value="">—</option>
                {DIMENSION_LEVELS.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
              </select>
            </div>
            <div style={fieldWrap}>
              <label style={labelSt}>{t.fIntegrity}</label>
              <select value={form.dimensions.integrity != null ? String(form.dimensions.integrity) : ''} onChange={setDim('integrity')} disabled={disabled} style={selectSt}>
                <option value="">—</option>
                {DIMENSION_LEVELS.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
              </select>
            </div>
            <div style={fieldWrap}>
              <label style={labelSt}>{t.fAvailability}</label>
              <select value={form.dimensions.availability != null ? String(form.dimensions.availability) : ''} onChange={setDim('availability')} disabled={disabled} style={selectSt}>
                <option value="">—</option>
                {DIMENSION_LEVELS.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* ── Section 3: Ownership ───────────────────────────────────────────── */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={sectionHeading}>{t.s3}</h3>
        <div style={grid2}>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fOwner} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
            <PeopleSelector orgId={org.id} lang={lang} value={form.owner} onChange={sf('owner')} disabled={disabled} />
            <ErrMsg msg={errors.owner} />
          </div>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fCustodian}</label>
            <PeopleSelector orgId={org.id} lang={lang} value={form.custodian} onChange={sf('custodian')} disabled={disabled} />
          </div>
        </div>
      </div>

      {/* ── Section 4: Location and relationships ─────────────────────────── */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={sectionHeading}>{t.s4}</h3>
        <div style={fieldWrap}>
          <label style={labelSt}>{t.fLocation}</label>
          <input value={form.location} onChange={hic('location')} disabled={disabled} style={inputSt(false)} />
        </div>
        <div style={fieldWrap}>
          <label style={labelSt}>{t.fLinkedSupplierIds}</label>
          {supplierOptions.length > 0 ? (
            <EntitySelector
              items={supplierOptions}
              selectedIds={form.linkedSupplierIds}
              onAdd={(id) => sf('linkedSupplierIds')([...form.linkedSupplierIds, id])}
              onRemove={(id) => sf('linkedSupplierIds')(form.linkedSupplierIds.filter((x) => x !== id))}
              placeholder={t.placeholderIds}
              disabled={disabled}
              chipBg={STATUS_INFO.bg}
              chipColor="#1d4ed8"
            />
          ) : (
            <TagInput
              tags={form.linkedSupplierIds}
              onAdd={(v) => sf('linkedSupplierIds')([...form.linkedSupplierIds, v])}
              onRemove={(v) => sf('linkedSupplierIds')(form.linkedSupplierIds.filter((x) => x !== v))}
              onUpdate={(oldValue, newValue) => sf('linkedSupplierIds')(form.linkedSupplierIds.map((x) => x === oldValue ? newValue : x))}
              placeholder={t.placeholderIds}
              disabled={disabled}
            />
          )}
        </div>
        <div style={fieldWrap}>
          <label style={labelSt}>{t.fLinkedDocumentIds}</label>
          {documentOptions.length > 0 ? (
            <EntitySelector
              items={documentOptions}
              selectedIds={form.linkedDocumentIds}
              onAdd={(id) => sf('linkedDocumentIds')([...form.linkedDocumentIds, id])}
              onRemove={(id) => sf('linkedDocumentIds')(form.linkedDocumentIds.filter((x) => x !== id))}
              placeholder={t.placeholderIds}
              disabled={disabled}
              chipBg={STATUS_INFO.bg}
              chipColor="#1d4ed8"
            />
          ) : (
            <TagInput
              tags={form.linkedDocumentIds}
              onAdd={(v) => sf('linkedDocumentIds')([...form.linkedDocumentIds, v])}
              onRemove={(v) => sf('linkedDocumentIds')(form.linkedDocumentIds.filter((x) => x !== v))}
              onUpdate={(oldValue, newValue) => sf('linkedDocumentIds')(form.linkedDocumentIds.map((x) => x === oldValue ? newValue : x))}
              placeholder={t.placeholderIds}
              disabled={disabled}
            />
          )}
        </div>
      </div>

      {/* ── Section 5: Lifecycle ───────────────────────────────────────────── */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={sectionHeading}>{t.s5}</h3>
        <div style={grid2}>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fStatus}</label>
            <select value={form.status} onChange={hic('status')} disabled={disabled} style={selectSt}>
              {ASSET_STATUSES.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fReviewDate}</label>
            <input type="date" value={form.reviewDate} onChange={hic('reviewDate')} disabled={disabled} style={inputSt(false)} />
          </div>
        </div>
      </div>

      </fieldset>

      {/* ── Sticky action bar ─────────────────────────────────────────────── */}
      {!isView && (
        <div style={{
          position: 'sticky',
          bottom: 0,
          marginLeft: '-2rem',
          marginRight: '-2rem',
          marginBottom: '-2rem',
          marginTop: '1.5rem',
          padding: '0.85rem 2rem',
          background: BG_SURFACE,
          borderTop: `1px solid ${BORDER_DEFAULT}`,
          borderBottomLeftRadius: 12,
          borderBottomRightRadius: 12,
          zIndex: 10,
        }}>
          {saveState === 'error' && (
            <div style={{ ...FONT_LABEL, background: ERROR_BG, color: ERROR_TEXT, border: `1px solid ${ERROR_BORDER}`, borderRadius: 6, padding: '0.35rem 0.75rem', marginBottom: '0.5rem' }}>
              {t.errorSave}
            </div>
          )}
          {deleteError && (
            <p style={{ ...FONT_LABEL, color: ERROR_TEXT, margin: '0 0 0.5rem' }}>
              {deleteError}
            </p>
          )}
          {Object.keys(errors).length > 0 && (
            <p style={{ ...FONT_LABEL, color: ERROR_TEXT, margin: '0 0 0.5rem' }}>
              {t.errorRequired}
            </p>
          )}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
            <div>
              {currentMode === 'edit' && (
                <button
                  onClick={() => setConfirmState({ confirmLabel: t.btnDelete })}
                  disabled={disabled}
                  style={{ ...BTN_DESTRUCTIVE_OUTLINE, ...FONT_LABEL, borderRadius: 8, padding: '0.5rem 1rem', cursor: disabled ? 'not-allowed' : 'pointer' }}
                >
                  {t.btnDelete}
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              {dirty && (
                <span style={{ ...FONT_LABEL, color: UNSAVED_AMBER }}>{t.unsavedLabel}</span>
              )}
              <button onClick={onCancel} disabled={disabled} style={btnSecondary}>{t.btnCancel}</button>
              <button onClick={handleSave} disabled={disabled} style={btnPrimary}>{saveState === 'saving' ? t.saving : saveState === 'ok' ? t.saveOk : t.btnSave}</button>
            </div>
          </div>
        </div>
      )}
      <SaveErrorDialog
        open={saveErrorOpen}
        lang={lang}
        onRetry={handleRetrySave}
        onDiscard={handleDiscardChanges}
      />
    </div>
  );
}
